import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Unsubscribe = () => {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";

  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!email || !token) {
      setStatus("error");
      setMessage("Missing email or token. Use the link from your email.");
      return;
    }
    (async () => {
      try {
        const url = `${API}/unsubscribe?email=${encodeURIComponent(
          email
        )}&token=${encodeURIComponent(token)}`;
        const res = await fetch(url, { method: "POST" });
        if (!res.ok) throw new Error("Invalid or expired link");
        setStatus("ok");
        setMessage(email);
      } catch (e) {
        setStatus("error");
        setMessage(
          e.message ||
            "We couldn't process your unsubscribe. Try the link again or email hello@operatoraihq.com."
        );
      }
    })();
  }, [email, token]);

  return (
    <div
      data-testid="unsubscribe-page"
      className="min-h-screen bg-white text-[#0A0A0A] flex flex-col"
    >
      <header className="border-b border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#0A0A0A] text-white font-extrabold text-sm tracking-tight">
              O
            </span>
            <span className="font-extrabold tracking-tight text-lg">
              OperatorAI
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm font-semibold text-[#525252] hover:text-[#0A0A0A]"
          >
            Back to operatoraihq.com
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-lg w-full text-center">
          {status === "loading" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#FAFAFA] border border-[#E5E5E5] mb-6">
                <span className="h-3 w-3 rounded-full bg-[#0052FF] animate-pulse" />
              </div>
              <h1
                data-testid="unsubscribe-loading"
                className="text-3xl font-extrabold tracking-tighter mb-3"
              >
                Processing your request…
              </h1>
              <p className="text-base text-[#525252]">One moment.</p>
            </>
          )}

          {status === "ok" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#0052FF]/10 text-[#0052FF] mb-6">
                <CheckCircle2 size={22} />
              </div>
              <h1
                data-testid="unsubscribe-success"
                className="text-3xl sm:text-4xl font-extrabold tracking-tighter mb-3"
              >
                You&apos;re unsubscribed.
              </h1>
              <p className="text-base text-[#525252]">
                <strong>{message}</strong> has been removed from the OperatorAI
                list. You won&apos;t receive any more emails from us.
              </p>
              <p className="text-sm text-[#525252] mt-6">
                Was this a mistake? You can re-subscribe anytime at{" "}
                <Link
                  to="/"
                  className="text-[#0052FF] font-semibold hover:underline"
                >
                  operatoraihq.com
                </Link>
                .
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600 mb-6">
                <AlertTriangle size={22} />
              </div>
              <h1
                data-testid="unsubscribe-error"
                className="text-3xl font-extrabold tracking-tighter mb-3"
              >
                We couldn&apos;t process that.
              </h1>
              <p className="text-base text-[#525252]">{message}</p>
              <a
                href="mailto:hello@operatoraihq.com?subject=Unsubscribe"
                className="mt-6 inline-flex items-center justify-center bg-[#0052FF] hover:bg-[#0040CC] text-white font-semibold py-3 px-5 rounded-lg transition-colors"
              >
                Email hello@operatoraihq.com
              </a>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Unsubscribe;
